import { itineraryApi } from '@/src/api/rtkQuery/itineraryApi'
import type { GiftVoucherDetail } from '@/src/types/couponTray'
import getItineraryEndpointPath from '@/src/utils/rtkQuery/getItineraryEndpointPath'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import fareFamilyApi from './fareFamilyApi'

/** Merge new vouchers into existing list by voucherCode (API returns only the newly applied voucher(s)). */
function mergeVouchersByCode(
    existing: GiftVoucherDetail[],
    incoming: GiftVoucherDetail[]
): GiftVoucherDetail[] {
    const byCode = new Set(existing.map(v => v.voucherCode))
    const merged = [...existing]
    for (const v of incoming) {
        if (v?.voucherCode && !byCode.has(v.voucherCode)) {
            merged.push(v)
            byCode.add(v.voucherCode)
        }
    }
    return merged
}

export interface ApplyGiftVoucherPayload {
    voucherDetails: { code: string; pin: string }[]
}

export type RemoveGiftVoucherPayload = { code: string; pin: string }[]

export const giftVoucherApi = createApi({
    reducerPath: 'giftVoucherApi',
    baseQuery: fetchBaseQuery({
        credentials: 'include',
        prepareHeaders: headers => {
            headers.set('Content-Type', 'application/json')
            return headers
        }
    }),
    endpoints: builder => ({
        applyGiftVoucher: builder.mutation<
            GiftVoucherDetail[],
            { itineraryId: string; payload: ApplyGiftVoucherPayload }
        >({
            query: ({ itineraryId, payload }) => ({
                url: getItineraryEndpointPath(itineraryId, `/itin/itinerary/${itineraryId}/applyVoucher`),
                method: 'POST',
                body: payload
            }),
            transformResponse: (response: { voucherData: GiftVoucherDetail[] }) => response.voucherData ?? [],
            async onQueryStarted({ itineraryId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    const raw = Array.isArray(data) ? data : []
                    const incoming = raw.filter(
                        (v): v is GiftVoucherDetail => v?.voucherStatus === 'SUCCESS'
                    )
                    dispatch(
                        itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                            if (draft?.itineraryData) {
                                const existing = draft.itineraryData.giftVoucherData ?? []
                                draft.itineraryData.giftVoucherData = mergeVouchersByCode(existing, incoming)
                            }
                        })
                    )
                } catch (e) {
                    // Do not clear cache on apply failure: already-applied voucher(s) should remain
                    console.error('Failed to update gift voucher cache after applyGiftVoucher:', e)
                } finally {
                    dispatch(fareFamilyApi.util.invalidateTags(['FareFamily']))
                }
            }
        }),

        removeGiftVoucher: builder.mutation<
            { success: boolean },
            { itineraryId: string; payload: RemoveGiftVoucherPayload }
        >({
            query: ({ itineraryId, payload }) => ({
                url: getItineraryEndpointPath(itineraryId, `/itin/itinerary/${itineraryId}/remove-voucher`),
                method: 'PUT',
                body: { voucherDetails: payload }
            }),
            transformResponse: (response: {
                responseCode?: number
                error?: unknown
                success?: boolean
                data?: string[]
                isSuccess?: boolean
            }) => {

                const success =
                    response?.responseCode === 200 &&
                    (response?.isSuccess === true || response?.success === true) &&
                    Array.isArray(response?.data)
                return { success }
            },
            async onQueryStarted({ itineraryId, payload }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    if (data?.success && Array.isArray(payload) && payload.length > 0) {
                        const codesToRemove = new Set(payload.map(p => p.code))
                        dispatch(
                            itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                                if (draft?.itineraryData?.giftVoucherData) {
                                    draft.itineraryData.giftVoucherData = draft.itineraryData.giftVoucherData.filter(
                                        v => !codesToRemove.has(v.voucherCode)
                                    )
                                }
                            })
                        )
                        dispatch(fareFamilyApi.util.invalidateTags(['FareFamily']))
                    }
                } catch (e) {
                    console.log('Failed to update gift voucher cache after removeGiftVoucher:', e)
                }
            }
        })
    })
})

export const { useApplyGiftVoucherMutation, useRemoveGiftVoucherMutation } = giftVoucherApi
export default giftVoucherApi

